// Screen 2, Capture. Two steps, and the whole path is manual: the caption is
// typed by the person who was there, nothing is generated. No AI touches
// this screen, per the Aug 2026 spec revision in docs/CONTRACT.md.
//
// Step 1 is the viewfinder: tap the shutter for a photo, press and hold for
// video capped at 60 seconds with a progress ring. A gallery upload input is
// always offered as well, because getUserMedia is not reachable in every
// WebView and the screen must still work when it is not.
//
// Step 2 is the caption sheet: caption, place, location and time, privacy.
//
// WRITING LATER IS A PRIMARY PATH, NOT A FALLBACK (round 4 addendum). Most
// entries are written at home, days after the trip, so the viewfinder offers
// an equally visible second way in that skips the camera entirely and goes
// straight here with a gallery upload, a location picker and a date picker.
//
// There is exactly ONE kind of entry. Live and later differ only in where the
// location and the time come from, never in what gets stored: no second data
// shape, no mode flag on the payload.
//
// Location priority: EXIF GPS from an uploaded photo, then address search,
// then a pin dropped on the map, then an existing destination. It is always
// overridable and always OPTIONAL, an entry with no location saves and simply
// gets no map pin. Time priority: EXIF DateTimeOriginal, then a manual
// picker, then now. `capturedAt` is when the traveller was THERE, so an entry
// written today about last month lands on last month in the diary.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Icon,
  IconCamera,
  IconChevronLeft,
  IconGallery,
  IconGlobe,
  IconLock,
  IconUnlock,
  IconRoute,
  IconTrash,
  IconVideo,
} from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { LocationPicker } from "../components/LocationPicker";
import { Segmented } from "../components/Segmented";
import { getGeoProvider } from "../lib/geo";
import { readExif } from "../lib/exif";
import { enqueueMoment, type PendingMedia } from "../lib/outbox";
import { drainOutbox } from "../lib/sync";
import { navigate } from "../lib/router";
import { uuid } from "../lib/uuid";
import { PRIVACY_VALUES, type Privacy } from "../lib/types";
import { useApp } from "../state/app";

const MAX_VIDEO_MS = 60_000;
/**
 * Contract cap: at most 5 photos per diary entry. Enforced HERE, before a
 * byte is uploaded, so a traveller is never told no after waiting on an
 * upload. The API rejects a sixth with a 400 as the backstop.
 */
export const MAX_MEDIA_PER_MOMENT = 5;
const HOLD_THRESHOLD_MS = 320;
const RING_CIRCUMFERENCE = 2 * Math.PI * 30;

type LocationSource = "gps" | "exif" | "manual" | "picked" | "none";

interface Draft {
  id: string;
  blob: Blob;
  mime: string;
  url: string;
  width: number | null;
  height: number | null;
}

/** Local ISO string for a datetime-local input, which rejects a Z suffix. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

function pickVideoMime(): string | undefined {
  // The API allowlist is image/jpeg, image/png, image/webp and video/mp4, so
  // ask for mp4 first. Browsers that cannot produce mp4 fall back to their
  // default container, which the server will reject, and the entry stays in
  // the outbox with a visible error rather than disappearing.
  const recorder = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!recorder?.isTypeSupported) return undefined;
  for (const candidate of ["video/mp4;codecs=avc1", "video/mp4"]) {
    if (recorder.isTypeSupported(candidate)) return candidate;
  }
  return undefined;
}

export function CaptureScreen() {
  const { t, activeTripId, defaultPrivacy } = useApp();
  const [step, setStep] = useState<"viewfinder" | "caption">("viewfinder");
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [cameraReady, setCameraReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const [caption, setCaption] = useState("");
  const [placeName, setPlaceName] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [capturedAt, setCapturedAt] = useState(() => toLocalInput(new Date().toISOString()));
  // Starts from the traveller's default, set once in Profil, and is still
  // changeable per entry below.
  const [privacy, setPrivacy] = useState<Privacy>(defaultPrivacy);
  const [locationSource, setLocationSource] = useState<LocationSource>("none");
  const [saving, setSaving] = useState(false);
  const [mediaFull, setMediaFull] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [exifPhotoName, setExifPhotoName] = useState<string | null>(null);
  const [timeSource, setTimeSource] = useState<"exif" | "manual" | "now">("now");

  // The coordinate the picker works with, derived from the two text fields
  // so manual typing and picking can never disagree.
  const parsedLat = lat.trim() === "" ? Number.NaN : Number(lat);
  const parsedLng = lng.trim() === "" ? Number.NaN : Number(lng);
  const picked =
    Number.isFinite(parsedLat) && Number.isFinite(parsedLng)
      ? { lat: parsedLat, lng: parsedLng }
      : null;
  const maxCapturedAt = toLocalInput(new Date().toISOString());
  const isFuture = capturedAt !== "" && new Date(capturedAt).getTime() > Date.now();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const didHoldRef = useRef(false);
  const draftsRef = useRef<Draft[]>([]);
  draftsRef.current = drafts;

  /**
   * Returns false when the entry is already full, so the caller can say so.
   * The capacity check reads the ref rather than the state updater, because
   * several files picked in one go are added inside a single tick and a
   * queued updater would not have run yet.
   */
  const addDraft = useCallback((draft: Draft) => {
    if (draftsRef.current.length >= MAX_MEDIA_PER_MOMENT) {
      URL.revokeObjectURL(draft.url);
      return false;
    }
    draftsRef.current = [...draftsRef.current, draft];
    setDrafts(draftsRef.current);
    return true;
  }, []);

  // Live camera. Torn down on unmount so the indicator light never lingers.
  useEffect(() => {
    if (step !== "viewfinder") return;
    let cancelled = false;
    const media = globalThis.navigator?.mediaDevices;
    if (!media?.getUserMedia) return;

    void media
      .getUserMedia({ video: { facingMode: "environment" }, audio: true })
      .then((stream) => {
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraReady(true);
      })
      .catch(() => setCameraReady(false));

    return () => {
      cancelled = true;
      for (const track of streamRef.current?.getTracks() ?? []) track.stop();
      streamRef.current = null;
      setCameraReady(false);
    };
  }, [step]);

  useEffect(
    () => () => {
      for (const draft of draftsRef.current) URL.revokeObjectURL(draft.url);
    },
    [],
  );

  async function stampFromGps(): Promise<void> {
    try {
      const fix = await getGeoProvider().getCurrent(8000);
      setLat(fix.lat.toFixed(6));
      setLng(fix.lng.toFixed(6));
      setCapturedAt(toLocalInput(fix.recordedAt));
      setLocationSource("gps");
    } catch {
      setLocationSource("none");
    }
  }

  async function takePhoto(): Promise<void> {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((result) => resolve(result), "image/jpeg", 0.9),
    );
    if (!blob) return;
    addDraft({
      id: uuid(),
      blob,
      mime: "image/jpeg",
      url: URL.createObjectURL(blob),
      width: canvas.width,
      height: canvas.height,
    });
    // GPS is read at shutter time, not at save time, so the stamp is the
    // place the photo was actually taken.
    await stampFromGps();
  }

  function stopRecording(): void {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    try {
      recorderRef.current?.stop();
    } catch {
      // Already stopped.
    }
  }

  function startRecording(): void {
    const stream = streamRef.current;
    const Recorder = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
    if (!stream || !Recorder) return;
    const mimeType = pickVideoMime();
    const recorder = new Recorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    chunksRef.current = [];
    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      setRecording(false);
      setElapsedMs(0);
      const type = mimeType?.split(";")[0] ?? "video/mp4";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (blob.size === 0) return;
      addDraft({ id: uuid(), blob, mime: type, url: URL.createObjectURL(blob), width: null, height: null });
      void stampFromGps();
    };
    recorder.start();
    setRecording(true);
    const startedAt = Date.now();
    tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    // Hard 60 second cap, enforced here rather than trusted to the user.
    stopTimerRef.current = setTimeout(stopRecording, MAX_VIDEO_MS);
  }

  function onShutterDown(): void {
    if (!cameraReady) return;
    didHoldRef.current = false;
    holdTimerRef.current = setTimeout(() => {
      didHoldRef.current = true;
      startRecording();
    }, HOLD_THRESHOLD_MS);
  }

  function onShutterUp(): void {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = null;
    if (didHoldRef.current) {
      stopRecording();
      return;
    }
    if (cameraReady) void takePhoto();
  }

  async function onFilesPicked(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;

    let rejected = false;
    for (const file of files) {
      const accepted = addDraft({
        id: uuid(),
        blob: file,
        mime: file.type || "image/jpeg",
        url: URL.createObjectURL(file),
        width: null,
        height: null,
      });
      if (!accepted) rejected = true;
    }
    setMediaFull(rejected);

    // An upload has no live fix, so the strongest signal is the photo's own
    // EXIF: it was taken at the place, whenever the entry gets written. The
    // FIRST photo that actually carries GPS wins, and its filename is shown
    // so the location never appears by magic.
    let located = false;
    let stamped = false;
    for (const file of files) {
      const exif = await readExif(file);
      if (!located && exif.lat !== null && exif.lng !== null) {
        setLat(exif.lat.toFixed(6));
        setLng(exif.lng.toFixed(6));
        setLocationSource("exif");
        setExifPhotoName(file.name || null);
        located = true;
      }
      if (!stamped && exif.capturedAt) {
        setCapturedAt(toLocalInput(exif.capturedAt));
        setTimeSource("exif");
        stamped = true;
      }
      if (located && stamped) break;
    }
    if (!located && locationSource === "none") setExifPhotoName(null);
    if (!stamped) {
      // No EXIF timestamp: the file's own modified date is a better guess
      // than "now" for an entry written later, and it stays editable.
      const fallback = files[0]!.lastModified;
      if (fallback) {
        setCapturedAt(toLocalInput(new Date(fallback).toISOString()));
        setTimeSource("manual");
      }
    }
  }

  function removeDraft(id: string): void {
    setDrafts((current) => {
      const target = current.find((draft) => draft.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return current.filter((draft) => draft.id !== id);
    });
  }

  async function save(): Promise<void> {
    // A location is optional, media is not: an entry is a photo or a video
    // plus the words that go with it. A future timestamp is refused here
    // because the API refuses it too.
    if (!activeTripId || drafts.length === 0 || isFuture || saving) return;
    setSaving(true);
    const media: PendingMedia[] = drafts.map((draft) => ({
      blob: draft.blob,
      mime: draft.mime,
      width: draft.width,
      height: draft.height,
      storageKey: null,
    }));

    // clientId is generated once here and is the idempotency key for every
    // retry of this entry, so a flaky connection can never create a duplicate.
    await enqueueMoment({
      clientId: uuid(),
      tripId: activeTripId,
      payload: {
        lat: picked?.lat ?? null,
        lng: picked?.lng ?? null,
        placeName: placeName.trim() || null,
        caption,
        capturedAt: fromLocalInput(capturedAt),
        privacy,
        media,
      },
    });

    void drainOutbox();
    const recordedUnsupportedVideo = drafts.some(
      (draft) => draft.mime.startsWith("video/") && !draft.mime.startsWith("video/mp4"),
    );
    setNotice(
      recordedUnsupportedVideo
        ? t("capture.videoUnsupported")
        : navigator.onLine === false
          ? t("capture.savedQueued")
          : t("capture.savedOnline"),
    );
    for (const draft of drafts) URL.revokeObjectURL(draft.url);
    setDrafts([]);
    setCaption("");
    setPlaceName("");
    setLat("");
    setLng("");
    setLocationSource("none");
    setExifPhotoName(null);
    setTimeSource("now");
    setCapturedAt(toLocalInput(new Date().toISOString()));
    setSaving(false);
    setStep("viewfinder");
    navigate("diary");
  }

  if (!activeTripId) {
    return (
      <div className="screen-pad">
        <EmptyState glyph={<IconRoute />} title={t("map.noTrip")} hint={t("map.noTripHint")} />
      </div>
    );
  }

  if (step === "caption") {
    return (
      <div className="screen-pad capture-caption">
        <div className="row-actions">
          <button type="button" className="btn btn-quiet" onClick={() => setStep("viewfinder")}>
            <Icon size={18}>
              <IconChevronLeft />
            </Icon>
            {t("capture.back")}
          </button>
        </div>

        {mediaFull || drafts.length >= MAX_MEDIA_PER_MOMENT ? (
          <p className="field-note" role="status">
            {t("capture.mediaFull")}
          </p>
        ) : null}

        <div className="thumb-grid">
          {drafts.map((draft) => (
            <figure key={draft.id} className="thumb">
              {draft.mime.startsWith("video/") ? (
                <video className="thumb-media" src={draft.url} muted playsInline />
              ) : (
                <img className="thumb-media" src={draft.url} alt="" />
              )}
              <button
                type="button"
                className="thumb-remove"
                onClick={() => removeDraft(draft.id)}
                aria-label={t("capture.remove")}
              >
                <Icon size={16}>
                  <IconTrash />
                </Icon>
              </button>
            </figure>
          ))}
        </div>

        <label className="field">
          <span className="field-label">{t("capture.caption")}</span>
          <textarea
            className="input input-area"
            rows={4}
            value={caption}
            placeholder={t("capture.captionPlaceholder")}
            onChange={(event) => setCaption(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">{t("capture.placeName")}</span>
          <input
            className="input"
            value={placeName}
            placeholder={t("capture.placeNamePlaceholder")}
            onChange={(event) => setPlaceName(event.target.value)}
          />
        </label>

        <div className="field">
          <span className="field-label">{t("capture.location")}</span>
          <p className="field-note">
            {locationSource === "gps"
              ? t("capture.locationFromGps")
              : locationSource === "exif"
                ? `${t("capture.locationFromExif")}${exifPhotoName ? `: ${exifPhotoName}` : ""}`
                : locationSource === "picked"
                  ? t("capture.locationPicked")
                  : locationSource === "manual"
                    ? t("capture.locationManual")
                    : t("capture.locationNone")}
          </p>

          <LocationPicker
            value={picked}
            onChange={(next) => {
              if (!next) {
                setLat("");
                setLng("");
                setLocationSource("none");
                setExifPhotoName(null);
                return;
              }
              setLat(next.lat.toFixed(6));
              setLng(next.lng.toFixed(6));
              setLocationSource("picked");
              setExifPhotoName(null);
              if (next.placeName && placeName.trim() === "") setPlaceName(next.placeName);
            }}
          />

          <div className="field-pair">
            <label className="field-sub">
              <span className="field-label-sm">{t("capture.latitude")}</span>
              <input
                className="input"
                inputMode="decimal"
                value={lat}
                onChange={(event) => {
                  setLat(event.target.value);
                  setLocationSource("manual");
                }}
              />
            </label>
            <label className="field-sub">
              <span className="field-label-sm">{t("capture.longitude")}</span>
              <input
                className="input"
                inputMode="decimal"
                value={lng}
                onChange={(event) => {
                  setLng(event.target.value);
                  setLocationSource("manual");
                }}
              />
            </label>
          </div>
        </div>

        <label className="field">
          <span className="field-label">{t("capture.time")}</span>
          <p className="field-note">
            {timeSource === "exif"
              ? t("capture.timeFromExif")
              : timeSource === "manual"
                ? t("capture.timeManual")
                : t("capture.timeNow")}
          </p>
          <input
            className="input"
            type="datetime-local"
            value={capturedAt}
            // The API rejects a future capturedAt with a 400, so the picker
            // refuses it here instead of letting the user find out on submit.
            max={maxCapturedAt}
            onChange={(event) => {
              setCapturedAt(event.target.value);
              setTimeSource("manual");
            }}
          />
          {isFuture ? (
            <p className="field-note" role="alert">
              {t("capture.timeFuture")}
            </p>
          ) : null}
        </label>

        <div className="field">
          <span className="field-label">{t("capture.privacy")}</span>
          <Segmented<Privacy>
            label={t("capture.privacy")}
            value={privacy}
            onChange={setPrivacy}
            options={PRIVACY_VALUES.map((value) => ({ value, label: t(`privacy.${value}`) }))}
          />
        </div>

        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={saving || drafts.length === 0 || isFuture}
          onClick={() => void save()}
        >
          {saving ? t("capture.saving") : t("capture.save")}
        </button>
        {notice ? <p className="field-note">{notice}</p> : null}
        <p className="field-note field-note-icon">
          <Icon size={14} color="var(--color-text-muted)">
            {privacy === "private" ? <IconLock /> : privacy === "friends" ? <IconUnlock /> : <IconGlobe />}
          </Icon>
          {t(`privacy.hint.${privacy}`)}
        </p>
      </div>
    );
  }

  const progress = Math.min(1, elapsedMs / MAX_VIDEO_MS);

  return (
    <div className="viewfinder">
      <div className="viewfinder-stage">
        <video
          ref={videoRef}
          className="viewfinder-video"
          autoPlay
          playsInline
          muted
          aria-label={t("capture.viewfinder")}
        />
        {!cameraReady ? (
          <p className="viewfinder-fallback">{t("capture.cameraUnavailable")}</p>
        ) : null}
        {recording ? (
          <p className="viewfinder-recording" role="status">
            {t("capture.recording")} {Math.floor(elapsedMs / 1000)}s
          </p>
        ) : null}
      </div>

      <div className="viewfinder-bar">
        <label className="btn-icon btn-icon-lg">
          <Icon size={22}>
            <IconGallery />
          </Icon>
          <span className="sr-only">{t("capture.gallery")}</span>
          <input
            className="sr-only"
            type="file"
            accept="image/*,video/mp4"
            capture="environment"
            multiple
            disabled={drafts.length >= MAX_MEDIA_PER_MOMENT}
            onChange={(event) => void onFilesPicked(event)}
          />
        </label>

        <button
          type="button"
          className={recording ? "shutter shutter-recording" : "shutter"}
          disabled={!cameraReady || drafts.length >= MAX_MEDIA_PER_MOMENT}
          onPointerDown={onShutterDown}
          onPointerUp={onShutterUp}
          onPointerLeave={onShutterUp}
          onPointerCancel={onShutterUp}
          aria-label={t("capture.shutter")}
        >
          <svg className="shutter-ring" viewBox="0 0 72 72" aria-hidden="true" focusable="false">
            <circle className="shutter-ring-track" cx="36" cy="36" r="30" />
            <circle
              className="shutter-ring-progress"
              cx="36"
              cy="36"
              r="30"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
            />
          </svg>
          <span className="shutter-core" />
        </button>

        <button
          type="button"
          className="btn-icon btn-icon-lg"
          disabled={drafts.length === 0}
          onClick={() => setStep("caption")}
          aria-label={t("capture.next")}
        >
          <Icon size={22}>
            {drafts.some((draft) => draft.mime.startsWith("video/")) ? <IconVideo /> : <IconCamera />}
          </Icon>
          {drafts.length > 0 ? <span className="badge-count">{drafts.length}</span> : null}
        </button>
      </div>

      {/* Writing later is a primary path: an equally visible way in that
          never opens the camera. */}
      <button
        type="button"
        className="btn btn-block viewfinder-later"
        onClick={() => {
          setTimeSource((current) => (current === "exif" ? current : "manual"));
          setStep("caption");
        }}
      >
        <Icon size={18}>
          <IconGallery />
        </Icon>
        {t("capture.writeLater")}
      </button>

      <p className="viewfinder-hint">
        {drafts.length === 0
          ? t("capture.shutterHint")
          : `${t("capture.selected")}: ${drafts.length} / ${MAX_MEDIA_PER_MOMENT}`}
      </p>
      <p className="viewfinder-hint viewfinder-hint-quiet">{t("capture.videoLimit")}</p>
    </div>
  );
}

